/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, September 23rd 2021, 11:42:03 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import * as React from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { OSAlert, OSLoadingButton, Spacer } from "ui-widgets";
import ErrorIcon from "@mui/icons-material/Error";

export interface HorizontalLinearStepperProps<TFieldData> extends React.HTMLProps<HTMLDivElement> {
  steps: {
    label: string;
    component: React.ReactNode;
    isOptional?: boolean;
    field_keys?: (keyof TFieldData)[];
  }[];
  isSubmitting: boolean;
  isSuccess: boolean;
  isError: boolean;
  validationErrors?: Record<keyof TFieldData, any>;
}

export default function HorizontalLinearStepper<TFieldData>({
  steps,
  isSubmitting = false,
  isSuccess = false,
  isError = false,
  validationErrors,
}: HorizontalLinearStepperProps<TFieldData>) {
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set<number>());

  const isStepOptional = (stepIndex: number) => {
    return steps[stepIndex].isOptional;
  };

  const stepHasError = (stepIndex: number) => {
    return steps[stepIndex].field_keys.filter((v) => validationErrors?.[v] !== undefined).length > 0;
  };

  const getStepIcon = (stepIndex: number) => stepHasError(stepIndex) && <ErrorIcon color="error" />;

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      // You probably want to guard against something like this,
      // it should never occur unless someone's actively trying to break something.
      throw new Error("You can't skip a step that isn't optional.");
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);

    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  // const handleReset = () => {
  //   setActiveStep(0);
  // };

  const isLastStep = activeStep === steps.length - 1;

  const isFirstStep = activeStep === 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Stepper activeStep={activeStep}>
        {steps.map((step, index) => {
          const stepProps: { completed?: boolean } = {};
          const labelProps: {
            optional?: React.ReactNode;
          } = {};

          if (isStepOptional(index)) {
            labelProps.optional = <Typography variant="caption">Optional</Typography>;
          }

          if (isStepSkipped(index)) {
            stepProps.completed = false;
          }

          return (
            <Step key={step.label} {...stepProps}>
              <StepLabel icon={getStepIcon(index)} {...labelProps}>
                {step.label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Spacer />
      {activeStep === steps.length ? (
        <React.Fragment>
          <OSAlert sx={{ mt: 2, mb: 1 }} severity="info">
            Steps completed. Save your data.
          </OSAlert>
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Box sx={{ flex: "1 1 auto" }} />
            <Button color="inherit" disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
            <OSLoadingButton variant="contained" isLoading={isSubmitting} type="submit">
              Save
            </OSLoadingButton>
          </Box>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <React.Fragment>{steps[activeStep].component}</React.Fragment>
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            {!isFirstStep ? (
              <Button
                variant="text"
                size="large"
                color="inherit"
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Back
              </Button>
            ) : null}
            <Box sx={{ flex: "1 1 auto" }} />
            {isStepOptional(activeStep) && (
              <Button size="large" color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                Skip
              </Button>
            )}
            {isLastStep ? (
              <React.Fragment>
                <OSLoadingButton variant="contained" isLoading={isSubmitting} type="submit">
                  Save
                </OSLoadingButton>
              </React.Fragment>
            ) : (
              <Button size="large" onClick={handleNext}>
                Next
              </Button>
            )}
          </Box>
        </React.Fragment>
      )}
    </Box>
  );
}
